import { formatFileSize } from '@/lib/schemas'

/**
 * Canonical IANA spellings. Compare case-insensitively: browsers ASCII-lowercase
 * `File.type` (Blob spec), so an upload carries `…macroenabled.12` while the
 * registration — and SharePoint — spell it `…macroEnabled.12`.
 */
export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const XLSM_MIME = 'application/vnd.ms-excel.sheet.macroEnabled.12'

/** Matches the `.xlsx` / `.xlsm` cap in ALLOWED_FILE_TYPES. */
export const MAX_WORKBOOK_BYTES = 20 * 1024 * 1024

/**
 * Is this an Open-XML workbook we can fill?
 *
 * Extension first, MIME as the fallback — browser and SharePoint uploads
 * routinely arrive as `application/octet-stream`, so testing MIME alone refuses
 * perfectly good workbooks. Mirrors `classifyScheduleSource` in
 * `backend/src/handlers/artifacts.ts`; keep the two in step.
 */
export function isWorkbook(filename: string, mimeType: string): boolean {
  const lowerName = filename.toLowerCase()
  const lowerMime = mimeType.toLowerCase()
  return (
    lowerName.endsWith('.xlsx') ||
    lowerName.endsWith('.xlsm') ||
    lowerMime === XLSX_MIME.toLowerCase() ||
    lowerMime === XLSM_MIME.toLowerCase()
  )
}

/**
 * Validate a file chosen as a schedule source. Returns a user-facing message, or
 * null when the file is acceptable.
 *
 * Legacy `.xls` gets its own message: the shared upload validator accepts it, but
 * the schedule handler hard-rejects it, so without this the user would upload
 * successfully and only fail at create time.
 */
export function validateWorkbookFile(file: File): string | null {
  if (file.name.toLowerCase().endsWith('.xls')) {
    return 'Legacy .xls workbooks are not supported. Save it as .xlsx and try again.'
  }
  if (!isWorkbook(file.name, file.type)) {
    return 'Choose an .xlsx or .xlsm workbook.'
  }
  if (file.size > MAX_WORKBOOK_BYTES) {
    return `That workbook is ${formatFileSize(file.size)}. The maximum is ${formatFileSize(MAX_WORKBOOK_BYTES)}.`
  }
  return null
}

/** 'Schedule of Rates.xlsx' → 'Schedule of Rates'. */
export function stripExtension(filename: string): string {
  return filename.replace(/\.[^.]+$/, '')
}
